/* agb-offers.js — panel-managed Offers card (free gift / sale) for the PDP.
 *
 * ONE file, byte-identical on ROW / EU / IL. Store differences come from the
 * theme globals at runtime, never from forked copies:
 *   window.AGB_OFFERS_CONFIG_URL     panel base + '/api/offers-config'
 *   window.AGB_STORE_REGION          'US-ROW' | 'International-EU' | 'IL' (X-Store-Region)
 *   window.AGB_PRESENTMENT_CURRENCY  {{ cart.currency.iso_code }} — money via Intl only
 *   window.AGB_LOCALE_ROOT           {{ routes.root_url }} — product data in the page language
 *   window.AGB_OFFERS_OFF = true     kill switch
 *
 * The one rule: the card never claims a price it cannot back. FREE / a sale
 * price render ONLY for an offer the server marks `live:true` (its Shopify
 * automatic discount is confirmed). When the panel cannot be reached right now,
 * the cached config is used but every offer is treated as not live, so the card
 * falls back to a plain full-price cross-sell or nothing.
 *
 * Timing (all from the public GET): render while startsAt <= now < hideAt. The
 * countdown counts to endsAt (the announced end); the card is removed at hideAt.
 * hideAt may be before endsAt (the card leaves early) or after it (the card
 * stays on after the countdown, which is then taken off the card): the server
 * keeps the Shopify discount running until hideAt in that case, so a live card
 * still never claims a price Shopify no longer gives. After hideAt the trigger
 * page shows the plain "Complete your setup" cross-sell when
 * display.afterEnd === 'cross_sell'.
 *
 * Placements:
 *   trigger  — the offer's trigger PDP (exact product id, else exact handle)
 *   gift     — the gift PDP (free_gift, live only): trigger card + gift note
 *   card     — a display.cardOn PDP: compact card linking to the trigger PDP
 *   after_end — trigger PDP after hideAt: plain cross-sell
 * Offers are tried in config order; one that cannot render on this page (not
 * live, gift missing / sold out, trigger not purchasable) yields to the next.
 * cardOn is matched by the handle in the URL. Handles are never translated on
 * these stores (verified 2026-08-30), so one handle matches every locale.
 *
 * Gift colour (gift.colourMap, free_gift): the gift colour pre-selected on the
 * trigger page follows the trigger variant selected in the product form,
 * looked up by VARIANT ID (byVariant[id], else defaultGiftVariantId) — never
 * by colour name, which differs per store and language (Black / Schwarz).
 * Mapped colour sold out -> default -> first available -> the sold-out teaser.
 * A colour the shopper picks by hand wins for the rest of the page view.
 * colourMap null = the shopper picks (first available colour pre-selected).
 *
 * Coexistence: free-ccw-offer.js keeps its own pages; when its card is on the
 * page this module does nothing. Adds go through ONE /cart/add.js
 * {items:[page, companion]} with a page-item-only fallback; cae-client.js
 * already counts that add, so no add_to_cart is emitted here.
 *
 * Every add path (a ticked card adds the companion ONCE per successful add of
 * the page product, never on a failed one):
 *   - Prestige product form submit: bindSubmit takes the submit over (above).
 *   - Any JSON add the page sends itself (gear builder #add-all-btn-2 /
 *     #add-all-btn / .js-add-all and the media-feed buttons that click them,
 *     easy-suggestion, its Storefront cartLinesAdd branch): a fetch "rider"
 *     appends the companion line to THAT request, so one request = one add of
 *     the page product = one companion, the path's own drawer refresh (read
 *     after the request) shows both lines, and a redirect after success cannot
 *     lose it. The path's own success signal is its response. A definite
 *     refusal (HTTP 4xx: sold out, the cart-quantity-cap 422; cartLinesAdd
 *     userErrors) commits nothing, so the ORIGINAL request is sent again and
 *     the shopper's own add proceeds. An unknown outcome (network rejection,
 *     status 0, 5xx) is never re-sent (the add may already be in the cart):
 *     the path gets a not-ok answer and shows its own error. The rider never
 *     rejects where the original would not.
 *   - Adds outside fetch (the ask card's XHR): its variant:add success event
 *     -> the companion alone, keepalive, then cart:refresh. A variant:add whose
 *     lines already hold the companion (a ridden response) adds nothing.
 * A card adds only while it is ticked AND on the page: at hideAt it is
 * removed and disarmed. Companion refused but the page item added -> the card
 * unticks and names the product that was not added. A companion behind the
 * terms / body-armor gate is never added from a card (link to its own page).
 */
(function () {
  'use strict';
  if (window.AGB_OFFERS_OFF) return;
  if (window.__agbOffersLoaded) return;
  window.__agbOffersLoaded = true;

  var LOG_PREFIX = '[agb-offers]';
  var SRC = 'agb-offers.js';
  var FETCH_TIMEOUT_MS = 8000;
  var MAX_TIMEOUT_MS = 2147483000;
  var BASE_CURRENCY = { 'US-ROW': 'USD', 'International-EU': 'EUR', 'IL': 'ILS' };

  // Live vs Preview storefront detection (copied from delivery-estimate.js).
  function _agbDetectStorefrontEnv() {
    try {
      var urlForce = new URLSearchParams(location.search).get('storefront_env');
      if (urlForce === 'preview' || urlForce === 'live') return urlForce;
    } catch (_) {}
    try {
      var stored = localStorage.getItem('AGB_StorefrontEnv');
      if (stored === 'preview' || stored === 'live') return stored;
    } catch (_) {}
    var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    if (role === 'main') return 'live';
    if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
    var host = (location && location.hostname) || '';
    if (host.indexOf('.shopifypreview.com', host.length - 20) !== -1) return 'preview';
    if (host.indexOf('.myshopify.com', host.length - 14) !== -1) return 'preview';
    if (host === '127.0.0.1' || host === 'localhost') return 'preview';
    try {
      if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
    } catch (_) {}
    return 'preview';
  }

  var ENV = _agbDetectStorefrontEnv();
  var LS_CONFIG_KEY = 'AGB_Offers_' + ENV;
  var LS_CONFIG_ETAG = 'AGB_OffersEtag_' + ENV;

  // Diagnostics print on PREVIEW only; window.__AGB_consoleCategories.offers === false silences.
  function _dbg() {
    try {
      if (ENV !== 'preview') return;
      var cats = window.__AGB_consoleCategories;
      if (cats && typeof cats === 'object' && cats.offers === false) return;
      console.log.apply(console, [LOG_PREFIX].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function ereReport(err, offerId, handle, critical) {
    try {
      if (!window.ERE) return;
      var fn = critical ? window.ERE.reportCritical : window.ERE.report;
      if (typeof fn !== 'function') return;
      fn.call(window.ERE, err, {
        source: SRC,
        severity: critical ? 'critical' : 'warn',
        context: { offerId: offerId || null, handle: handle || null }
      });
    } catch (e) {}
  }

  // CAE: self-waiting emit (CAEDev loads deferred too); neutral names only.
  function caeEmit(name, detail, tries) {
    try {
      if (window.CAE_DISABLED) return;
      if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit(name, detail);
        return;
      }
    } catch (e) { return; }
    if ((tries || 0) > 40) return;
    setTimeout(function () { caeEmit(name, detail, (tries || 0) + 1); }, 200);
  }

  // ===== Language + strings ================================================

  function docLangFull() {
    try { return String((document.documentElement && document.documentElement.lang) || 'en').toLowerCase(); }
    catch (e) { return 'en'; }
  }
  function docLang() { return docLangFull().split('-')[0] || 'en'; }
  function isRtl() { return docLang() === 'he'; }

  var STRINGS = {
    en: {
      endsIn: 'Offer ends in', free: 'FREE', soldOut: 'Sold out',
      addFree: 'Add for free to your order', addToOrder: 'Add to your order',
      completeSetup: 'Complete your setup', color: 'Color', sale: 'Sale',
      giftPage: 'Add the {trigger} — get the {gift} free',
      giftNote: 'FREE when you add it with the {trigger}',
      giftNoteInCart: '✓ FREE — it’s on us with your {trigger} (already in your cart)',
      cardFree: 'Get the {gift} FREE with the {trigger}',
      viewOffer: 'View offer', view: 'View {name}',
      addError: 'We couldn’t add this to your cart. Please try again.',
      compRefused: 'The {name} could not be added to your cart.',
      day: 'day', days: 'days'
    },
    de: {
      endsIn: 'Angebot endet in', free: 'GRATIS', soldOut: 'Ausverkauft',
      addFree: 'Gratis zu deiner Bestellung hinzufügen', addToOrder: 'Zu deiner Bestellung hinzufügen',
      completeSetup: 'Vervollständige dein Setup', color: 'Farbe', sale: 'Angebot',
      giftPage: '{trigger} kaufen — {gift} gratis dazu',
      giftNote: 'GRATIS zusammen mit {trigger}',
      giftNoteInCart: '✓ GRATIS — mit deinem {trigger} (bereits im Warenkorb)',
      cardFree: '{gift} GRATIS zu {trigger}',
      viewOffer: 'Zum Angebot', view: '{name} ansehen',
      addError: 'Das konnte nicht in den Warenkorb gelegt werden. Bitte versuche es erneut.',
      compRefused: '{name} konnte nicht in den Warenkorb gelegt werden.',
      day: 'Tag', days: 'Tage'
    },
    fr: {
      endsIn: 'L’offre se termine dans', free: 'GRATUIT', soldOut: 'Épuisé',
      addFree: 'Ajoutez-le gratuitement à votre commande', addToOrder: 'Ajouter à votre commande',
      completeSetup: 'Complétez votre équipement', color: 'Couleur', sale: 'Promo',
      giftPage: 'Ajoutez {trigger} — {gift} offert',
      giftNote: 'GRATUIT avec {trigger}',
      giftNoteInCart: '✓ GRATUIT — offert avec votre {trigger} (déjà dans votre panier)',
      cardFree: '{gift} GRATUIT avec {trigger}',
      viewOffer: 'Voir l’offre', view: 'Voir {name}',
      addError: 'Impossible d’ajouter au panier. Veuillez réessayer.',
      compRefused: 'Impossible d’ajouter {name} à votre panier.',
      day: 'jour', days: 'jours'
    },
    es: {
      endsIn: 'La oferta termina en', free: 'GRATIS', soldOut: 'Agotado',
      addFree: 'Añádelo gratis a tu pedido', addToOrder: 'Añadir a tu pedido',
      completeSetup: 'Completa tu equipo', color: 'Color', sale: 'Oferta',
      giftPage: 'Añade {trigger} — {gift} gratis',
      giftNote: 'GRATIS al añadirlo con {trigger}',
      giftNoteInCart: '✓ GRATIS — con tu {trigger} (ya en tu carrito)',
      cardFree: '{gift} GRATIS con {trigger}',
      viewOffer: 'Ver oferta', view: 'Ver {name}',
      addError: 'No se pudo añadir a tu carrito. Inténtalo de nuevo.',
      compRefused: 'No se pudo añadir {name} a tu carrito.',
      day: 'día', days: 'días'
    },
    he: {
      endsIn: 'המבצע מסתיים בעוד', free: 'חינם', soldOut: 'אזל מהמלאי',
      addFree: 'הוסיפו להזמנה בחינם', addToOrder: 'הוסיפו להזמנה',
      completeSetup: 'השלימו את הציוד', color: 'צבע', sale: 'מבצע',
      giftPage: 'הוסיפו {trigger} — {gift} בחינם',
      giftNote: 'חינם בהוספה יחד עם {trigger}',
      giftNoteInCart: '✓ חינם — עם {trigger} שכבר בעגלה',
      cardFree: '{gift} בחינם עם {trigger}',
      viewOffer: 'לצפייה במבצע', view: 'לצפייה ב{name}',
      addError: 'לא הצלחנו להוסיף לעגלה. נסו שוב.',
      compRefused: 'לא ניתן היה להוסיף את {name} לעגלה.',
      day: 'יום', days: 'ימים'
    }
  };

  function str(key) {
    var t = STRINGS[docLang()] || STRINGS.en;
    return (typeof t[key] === 'string') ? t[key] : STRINGS.en[key];
  }

  // Standout text from config: exact lang -> base lang -> en -> hidden.
  function standoutFor(offer) {
    var map = offer && offer.display && offer.display.standoutText;
    if (!map || typeof map !== 'object') return '';
    var full = docLangFull(), base = docLang();
    if (typeof map[full] === 'string' && map[full]) return map[full];
    if (typeof map[base] === 'string' && map[base]) return map[base];
    if (typeof map.en === 'string' && map.en) return map.en;
    return '';
  }

  // ===== DOM helpers (textContent only — never config text through innerHTML) =

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = String(text);
    return n;
  }

  // A run the bidi algorithm must not reorder: Latin names / money / digits in RTL.
  function hasHebrew(s) { return /[֐-׿]/.test(String(s || '')); }
  function nameNode(name) {
    var s = el('span', 'agb-offer__pname', name);
    s.setAttribute('dir', (isRtl() && !hasHebrew(name)) ? 'ltr' : 'auto');
    return s;
  }
  function ltrNode(text, cls) {
    var s = el('span', cls || '', text);
    s.setAttribute('dir', 'ltr');
    return s;
  }

  // Fill `host` from a template with {key} slots; vars[key] is a Node or a string.
  function fill(host, template, vars) {
    var re = /\{(\w+)\}/g, last = 0, m;
    while ((m = re.exec(template)) !== null) {
      if (m.index > last) host.appendChild(document.createTextNode(template.slice(last, m.index)));
      var v = vars && vars[m[1]];
      if (v && typeof v === 'object' && v.nodeType) host.appendChild(v);
      else host.appendChild(document.createTextNode(v == null ? '' : String(v)));
      last = re.lastIndex;
    }
    if (last < template.length) host.appendChild(document.createTextNode(template.slice(last)));
    return host;
  }

  // ===== Money (Intl only — never a hardcoded symbol) ======================

  function presentmentCurrency() {
    var c = '';
    try { c = window.AGB_PRESENTMENT_CURRENCY || ''; } catch (e) {}
    if (!c) { try { c = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''; } catch (e) {} }
    return String(c || '').toUpperCase();
  }
  // Returns null when no currency is known: the price line is then omitted.
  function fmtMoney(cents) {
    var cur = presentmentCurrency();
    if (!cur || typeof cents !== 'number' || !isFinite(cents)) return null;
    try {
      return new Intl.NumberFormat(docLang(), { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) { return null; }
  }
  function moneyNode(cents, cls) {
    var s = fmtMoney(cents);
    return s == null ? null : ltrNode(s, 'agb-offer__money' + (cls ? ' ' + cls : ''));
  }
  function fmtPercentOff(value) {
    try { return new Intl.NumberFormat(docLang(), { style: 'percent', maximumFractionDigits: 0 }).format(-value / 100); }
    catch (e) { return null; }
  }

  // ===== Time ==============================================================

  function parseMs(iso) { var t = Date.parse(iso); return isNaN(t) ? NaN : t; }
  function windowOf(offer) {
    var tm = offer.timing || {};
    var start = parseMs(tm.startsAt), end = parseMs(tm.endsAt), hide = parseMs(tm.hideAt);
    if (isNaN(hide)) hide = end;   // no hideAt: the card goes when the countdown ends
    return { start: start, end: end, hide: hide };
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function renderCountdown(host, ms) {
    if (ms < 0) ms = 0;
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    while (host.firstChild) host.removeChild(host.firstChild);
    if (d > 0) {
      host.appendChild(ltrNode(String(d), 'agb-offer__cd-days'));
      host.appendChild(document.createTextNode(' ' + (d === 1 ? str('day') : str('days')) + ' '));
    }
    host.appendChild(ltrNode(pad2(h) + ':' + pad2(m) + ':' + pad2(s), 'agb-offer__cd-clock'));
  }

  // ===== Page identity =====================================================

  function pageHandle() {
    var p = '';
    try { p = location.pathname || ''; } catch (e) {}
    var i = p.indexOf('/products/');
    if (i === -1) return '';
    var rest = p.slice(i + 10), cut = rest.search(/[\/?#]/);
    if (cut !== -1) rest = rest.slice(0, cut);
    try { rest = decodeURIComponent(rest); } catch (e) {}
    return rest.toLowerCase();
  }
  function pageProductId() {
    try {
      var id = window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id;
      var n = parseInt(id, 10);
      return n > 0 ? n : 0;
    } catch (e) { return 0; }
  }
  // Exact id when both sides know it, else exact handle (never a prefix match).
  function isPage(ref, pid, handle) {
    if (!ref) return false;
    var rid = parseInt(ref.productId, 10);
    if (pid && rid > 0) return pid === rid;
    return !!handle && String(ref.handle || '').toLowerCase() === handle;
  }

  function localeRoot() {
    var r = '';
    try { r = window.AGB_LOCALE_ROOT || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || ''; } catch (e) {}
    if (!r) return '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  function pdpUrl(handle) { return localeRoot() + 'products/' + encodeURIComponent(handle); }

  // ===== Product data ======================================================

  var productCache = {};
  // Resolves {status:'ok', product} | {status:'missing'} (404) | {status:'error'}.
  function fetchProduct(handle) {
    var key = localeRoot() + '|' + handle;
    if (productCache[key]) return productCache[key];
    var p = fetch(pdpUrl(handle) + '.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 404) return { status: 'missing' };
        if (!r.ok) return { status: 'error', http: r.status };
        return r.json().then(function (j) {
          return (j && j.variants && j.variants.length) ? { status: 'ok', product: j } : { status: 'missing' };
        });
      })
      .catch(function (err) { return { status: 'error', err: err }; });
    productCache[key] = p;
    return p;
  }
  function inStock(p) {
    return !!(p && p.variants && p.variants.some(function (v) { return v && v.available; }));
  }
  function firstAvailable(p) {
    if (!p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].available) return p.variants[i];
    return null;
  }
  function variantById(p, id) {
    if (!p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].id === id) return p.variants[i];
    return null;
  }
  // ===== Gift colour follows the trigger colour (gift.colourMap) ============
  // Keyed by the TRIGGER variant id. The public GET ships only byVariant and
  // defaultGiftVariantId; anything else in the object is ignored.
  function colourMapOf(offer) {
    var m = offer && offer.gift && offer.gift.colourMap;
    if (!m || typeof m !== 'object') return null;
    var by = (m.byVariant && typeof m.byVariant === 'object') ? m.byVariant : {};
    var def = parseInt(m.defaultGiftVariantId, 10);
    return { byVariant: by, defaultId: def > 0 ? def : 0 };
  }
  // mapped -> default -> first available; null = no colour available at all.
  function giftVariantFor(p, map, triggerVariantId) {
    var key = String(triggerVariantId || '');
    var want = (key && Object.prototype.hasOwnProperty.call(map.byVariant, key)) ? parseInt(map.byVariant[key], 10) : 0;
    var v = want > 0 ? variantById(p, want) : null;
    if (v && v.available) return v;
    v = map.defaultId ? variantById(p, map.defaultId) : null;
    if (v && v.available) return v;
    return firstAvailable(p);
  }
  // offerId|giftHandle -> the gift variant id the shopper chose by hand. Module
  // scope so the choice survives re-renders (revalidation) in this page view.
  var giftPicks = {};
  // offerId|placement -> { live } while the shopper has the card ticked. Module
  // scope so a re-render of the same card (revalidation) keeps the tick.
  var ticks = {};

  // Behind the add-to-cart terms / body-armor gate (terms-agreement-gate.liquid
  // + terms-agreement-gate.js)? Such a product is never added from a card: the
  // gate only sees the page's own loadout. Any signal counts (fail closed).
  var TERMS_HANDLES = ['ops-core-fast-sf-next-gen-ballistic-helmet'];
  function gatedProduct(p, handle) {
    var h = String((p && p.handle) || handle || '').toLowerCase();
    if (h && TERMS_HANDLES.indexOf(h) !== -1) return true;
    var tags = p && p.tags;
    if (typeof tags === 'string') tags = tags.split(',');
    if (Array.isArray(tags) && tags.some(function (t) { return String(t || '').trim().toLowerCase() === 'requires-terms-agreement'; })) return true;
    if (h && /armor[-\s]?plate|ceramic[-\s]?plate|soft[-\s]?armor|armor[-\s]?insert/i.test(h)) return true;
    try { if (h && typeof window.AGB_isPlate === 'function' && window.AGB_isPlate(h) === true) return true; } catch (e) {}
    return false;
  }

  function imageUrl(p, v) {
    var img = (v && v.featured_image && v.featured_image.src) || (p && (p.featured_image || (p.images && p.images[0]))) || '';
    var url = (typeof img === 'string') ? img : (img && img.src) || '';
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  }

  // ===== Product form ======================================================

  // The REAL add-to-cart form (id "product-form-…"), never the Shop Pay
  // installment form that shares action=/cart/add. getAttribute('id') because
  // form.id is shadowed by Prestige's <input name="id">.
  function productForm() {
    var f = document.querySelector('form[id^="product-form-"]');
    return f || null;
  }
  function formId(form) { return (form && form.getAttribute('id')) || ''; }
  function formField(form, name) {
    var fid = formId(form);
    return form.querySelector('[name="' + name + '"]') ||
      (fid ? document.querySelector('[name="' + name + '"][form="' + fid + '"]') : null);
  }
  function submitButtons(form) {
    var out = [], fid = formId(form);
    function add(list) { for (var i = 0; i < list.length; i++) if (out.indexOf(list[i]) === -1) out.push(list[i]); }
    if (fid) add(document.querySelectorAll('[type="submit"][form="' + fid + '"], [name="add"][form="' + fid + '"]'));
    add(form.querySelectorAll('[type="submit"], [name="add"]'));
    return out;
  }
  // Line-item properties on the form (e.g. the terms checkbox) ride along with the page item.
  function formProperties(form) {
    var props = {}, any = false, fid = formId(form);
    var nodes = [].slice.call(form.querySelectorAll('[name^="properties["]'));
    if (fid) nodes = nodes.concat([].slice.call(document.querySelectorAll('[name^="properties["][form="' + fid + '"]')));
    nodes.forEach(function (n) {
      if (n.disabled) return;
      var type = String(n.getAttribute('type') || '').toLowerCase();
      if ((type === 'checkbox' || type === 'radio') && !n.checked) return;
      var m = /^properties\[(.*)\]$/.exec(n.getAttribute('name') || '');
      if (!m) return;
      props[m[1]] = n.value;
      any = true;
    });
    return any ? props : null;
  }
  function selectedVariantId(form) {
    var f = form && formField(form, 'id');
    var n = f ? parseInt(f.value, 10) : 0;
    return n > 0 ? n : 0;
  }
  function pageQuantity(form) {
    var q = formField(form, 'quantity');
    var n = q ? parseInt(q.value, 10) : 1;
    return (n > 0) ? n : 1;
  }
  function freeCcwPresent() { return !!document.querySelector('.free-ccw-offer, .free-ccw-suggest'); }

  function mountPoint(form) {
    var fid = formId(form);
    var picker = (fid && document.querySelector('variant-picker[form="' + fid + '"]')) ||
      document.querySelector('variant-picker, variant-radios, variant-selects, .variant-picker, [id^="variant-radios"]');
    if (picker && picker.parentNode) return { parent: picker.parentNode, before: picker.nextSibling };
    var bb = fid && document.querySelector('buy-buttons[form="' + fid + '"]');
    if (bb && bb.parentNode) return { parent: bb.parentNode, before: bb };
    var atc = form.querySelector('[name="add"]');
    if (atc && atc.parentNode) return { parent: atc.parentNode, before: atc };
    return { parent: form, before: null };
  }

  // ===== Cart ==============================================================

  // keepalive: the request outlives a navigation that follows the add.
  function addItems(items, keepalive) {
    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    };
    if (keepalive) init.keepalive = true;
    var req;
    selfPost++;   // the rider lets this module's own adds through untouched
    try { req = fetch(localeRoot() + 'cart/add.js', init); }
    finally { selfPost--; }
    return req.then(function (r) {
      if (r.ok) return r.json();
      var e = new Error('cart/add.js HTTP ' + r.status); e.http = r.status;
      throw e;
    });
  }
  function getCart() {
    return fetch(localeRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }
  function afterAdd() {
    try {
      var tv = window.themeVariables && window.themeVariables.settings;
      if (tv && (tv.cartType === 'page' || tv.pageType === 'cart')) { location.href = localeRoot() + 'cart'; return; }
    } catch (e) {}
    try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
    try {
      var d = document.querySelector('cart-drawer');
      if (d && typeof d.show === 'function') d.show();
    } catch (e) {}
  }

  // ===== Styles ============================================================

  function injectStyle() {
    if (document.getElementById('agb-offer-style')) return;
    var s = document.createElement('style');
    s.id = 'agb-offer-style';
    s.textContent =
      '.agb-offer{margin-block:18px;border:1px solid rgba(28,28,28,.16);border-radius:2px;background:#fafafa;overflow:hidden;color:#1c1c1c;text-align:start}' +
      '.agb-offer__standout{margin:0;padding-block:8px;padding-inline:14px;background:#8f1814;color:#fff;font-size:.78rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase}' +
      '.agb-offer__timer{display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;padding-block:12px 0;padding-inline:14px;font-size:.92rem;font-weight:700}' +
      '.agb-offer__countdown{font-variant-numeric:tabular-nums;font-size:1.05rem;font-weight:900;letter-spacing:.03em;color:#8f1814}' +
      '.agb-offer__body{padding:14px}' +
      '.agb-offer__eyebrow{margin-block:0 8px;font-size:.78rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#8f1814}' +
      '.agb-offer--plain .agb-offer__eyebrow{color:#5a5a5a}' +
      '.agb-offer__row{display:flex;align-items:center;gap:12px;width:100%;padding-block:10px;padding-inline:12px;border:2px solid rgba(28,28,28,.14);border-radius:2px;background:#fff;box-sizing:border-box}' +
      '.agb-offer__row[role="button"]{cursor:pointer;transition:border-color .15s ease,background .15s ease}' +
      '.agb-offer__row[role="button"]:hover,.agb-offer__row.is-selected{border-color:#1c1c1c}' +
      '.agb-offer__row.is-selected{background:rgba(146,174,116,.14)}' +
      '.agb-offer__thumb-link{display:block;flex:0 0 auto;line-height:0;overflow:hidden}' +
      '.agb-offer__thumb{width:52px;height:52px;object-fit:cover;background:#f3f4f6}' +
      '.agb-offer__info{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 auto}' +
      '.agb-offer__name{align-self:flex-start;max-width:100%;font-weight:700;font-size:.98rem;color:#1c1c1c;text-decoration:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      '.agb-offer__name:hover{text-decoration:underline}' +
      '.agb-offer__price{font-size:.9rem;color:#5a5a5a}' +
      '.agb-offer__was{color:#8a8a8a;margin-inline-end:6px}' +
      '.agb-offer__freeword,.agb-offer__now{color:#8f1814;font-weight:800;letter-spacing:.03em}' +
      '.agb-offer__badge{display:inline-block;margin-inline-end:8px;padding-block:2px;padding-inline:8px;background:#8f1814;color:#fff;font-size:.75rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase}' +
      '.agb-offer__check{flex:0 0 auto;position:relative;width:24px;height:24px;border:2px solid #cfcfcf;box-sizing:border-box;transition:background .15s ease,border-color .15s ease}' +
      '.agb-offer__row.is-selected .agb-offer__check{background:#92ae74;border-color:#92ae74}' +
      '.agb-offer__row.is-selected .agb-offer__check::after{content:"";position:absolute;inset:0;margin:auto;width:5px;height:10px;border:solid #fff;border-width:0 2px 2px 0;transform:translateY(-1px) rotate(45deg)}' +
      '.agb-offer__soldout{flex:0 0 auto;padding-block:4px;padding-inline:10px;font-size:.72rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#8f1814;background:rgba(143,24,20,.08);white-space:nowrap}' +
      '.agb-offer--soldout .agb-offer__row{cursor:default}' +
      '.agb-offer__opts{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:10px 0 0;padding:0;border:0}' +
      '.agb-offer__opts-legend{float:inline-start;margin-inline-end:6px;padding:0;font-size:.82rem;font-weight:700}' +
      '.agb-offer__opt{position:relative;display:inline-flex}' +
      '.agb-offer__opt input{position:absolute;opacity:0;inset:0;margin:0;cursor:pointer}' +
      '.agb-offer__opt span{display:inline-block;padding-block:4px;padding-inline:10px;border:1px solid rgba(28,28,28,.25);background:#fff;font-size:.82rem}' +
      '.agb-offer__opt input:checked+span{border-color:#1c1c1c;box-shadow:inset 0 0 0 1px #1c1c1c}' +
      '.agb-offer__opt input:focus-visible+span{outline:2px solid #1c1c1c;outline-offset:2px}' +
      '.agb-offer__opt input:disabled+span{opacity:.45;text-decoration:line-through}' +
      '.agb-offer__opt input:disabled{cursor:default}' +
      '.agb-offer__cta{flex:0 0 auto;font-size:.82rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#1c1c1c}' +
      '.agb-offer__error{margin-block:10px 0;font-size:.85rem;font-weight:700;color:#8f1814}' +
      '.agb-offer-note{margin-block:10px;font-weight:700;color:#1c1c1c;text-align:start}' +
      '.agb-offer-note.is-free{color:#8f1814}';
    (document.head || document.documentElement).appendChild(s);
  }

  // ===== Render state ======================================================

  var gen = 0;          // bumps on every re-render; stale async work bails
  var mounted = null;   // { nodes:[], intervals:[], timeouts:[], listeners:[] }

  function teardown() {
    armed = null;
    if (!mounted) return;
    mounted.intervals.forEach(function (i) { clearInterval(i); });
    mounted.timeouts.forEach(function (t) { clearTimeout(t); });
    mounted.listeners.forEach(function (l) { try { l[0].removeEventListener(l[1], l[2], l[3]); } catch (e) {} });
    mounted.nodes.forEach(function (n) { if (n && n.parentNode) n.parentNode.removeChild(n); });
    mounted = null;
  }
  function track() {
    if (!mounted) mounted = { nodes: [], intervals: [], timeouts: [], listeners: [] };
    return mounted;
  }
  function listen(target, type, fn, opt) {
    target.addEventListener(type, fn, opt);
    track().listeners.push([target, type, fn, opt]);
  }
  function later(fn, ms) {
    if (!(ms >= 0)) return;
    track().timeouts.push(setTimeout(fn, Math.min(ms, MAX_TIMEOUT_MS)));
  }

  // ===== Card builders =====================================================

  /* One card. opts:
   *  offer, placement ('trigger'|'gift'|'card'|'after_end'), live (bool),
   *  companion (product JSON — what the card shows / adds), companionHandle,
   *  claimFree (bool), eyebrow (Node|string), showTimer, endsMs,
   *  selectable (bool), linkOnly (bool), priceMode ('free'|'full'|'sale'|'none'),
   *  pageProduct (sale), sale config,
   *  giftColour ({map, form, key} — the companion is the gift on the trigger
   *  page and its colour follows the trigger variant; omitted = shopper picks).
   */
  function buildCard(o) {
    var offer = o.offer;
    var wrap = el('div', 'agb-offer');
    wrap.setAttribute('data-agb-offer', offer.id || '');
    wrap.setAttribute('data-placement', o.placement);
    if (!o.claimFree && o.priceMode !== 'sale') wrap.classList.add('agb-offer--plain');
    if (isRtl()) wrap.setAttribute('dir', 'rtl');

    var standout = o.live ? standoutFor(offer) : '';
    if (standout) {
      var so = el('p', 'agb-offer__standout', standout);
      so.setAttribute('dir', 'auto');
      wrap.appendChild(so);
    }

    var timerEl = null, cdEl = null;
    // No countdown once its end has passed (a card kept on after the end).
    if (o.showTimer && !isNaN(o.endsMs) && o.endsMs > Date.now()) {
      timerEl = el('div', 'agb-offer__timer');
      timerEl.setAttribute('role', 'timer');
      timerEl.appendChild(el('span', 'agb-offer__timer-label', str('endsIn')));
      cdEl = el('span', 'agb-offer__countdown');
      timerEl.appendChild(cdEl);
      wrap.appendChild(timerEl);
      renderCountdown(cdEl, o.endsMs - Date.now());
    }

    var body = el('div', 'agb-offer__body');
    wrap.appendChild(body);
    if (o.eyebrow) {
      var eb = el('p', 'agb-offer__eyebrow');
      if (typeof o.eyebrow === 'string') eb.textContent = o.eyebrow; else eb.appendChild(o.eyebrow);
      body.appendChild(eb);
    }

    var p = o.companion;
    var row = el('div', 'agb-offer__row');
    body.appendChild(row);
    var url = pdpUrl(o.companionHandle);
    var thumbLink = el('a', 'agb-offer__thumb-link');
    thumbLink.setAttribute('href', url);
    thumbLink.setAttribute('tabindex', '-1');
    thumbLink.setAttribute('aria-hidden', 'true');
    var thumb = el('img', 'agb-offer__thumb');
    thumb.setAttribute('alt', '');
    thumb.setAttribute('loading', 'lazy');
    thumbLink.appendChild(thumb);
    row.appendChild(thumbLink);

    var info = el('span', 'agb-offer__info');
    var nameLink = el('a', 'agb-offer__name');
    nameLink.setAttribute('href', url);
    nameLink.appendChild(nameNode(p.title || o.companionHandle));
    nameLink.setAttribute('aria-label', str('view').replace('{name}', p.title || o.companionHandle));
    info.appendChild(nameLink);
    var priceEl = el('span', 'agb-offer__price');
    info.appendChild(priceEl);
    row.appendChild(info);
    [thumbLink, nameLink].forEach(function (a) { a.addEventListener('click', function (e) { e.stopPropagation(); }); });

    var state = { selected: false, variant: firstAvailable(p) || p.variants[0], wrap: wrap, row: row, userPicked: false, product: p };
    var gc = o.giftColour || null;
    if (gc) {
      // The shopper's own earlier pick (this page view) wins; else follow the trigger variant.
      var picked = giftPicks[gc.key] ? variantById(p, giftPicks[gc.key]) : null;
      if (picked && picked.available) { state.variant = picked; state.userPicked = true; }
      else {
        delete giftPicks[gc.key];
        gc.lastTrigger = selectedVariantId(gc.form);
        state.variant = giftVariantFor(p, gc.map, gc.lastTrigger) || state.variant;
      }
    }

    function paintThumb() {
      var u = imageUrl(p, state.variant);
      if (u) { thumb.setAttribute('src', u); thumb.setAttribute('alt', p.title || ''); thumb.hidden = false; }
      else thumb.hidden = true;
    }
    function paintPrice() {
      while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);
      var mode = o.priceMode;
      if (mode === 'none') { priceEl.hidden = true; return; }
      priceEl.hidden = false;
      if (mode === 'free') {
        var was = (offer.display && offer.display.showPrice) ? moneyNode(state.variant && state.variant.price, 'agb-offer__was') : null;
        if (was) { var sEl = el('s'); sEl.appendChild(was); priceEl.appendChild(sEl); }
        priceEl.appendChild(el('b', 'agb-offer__freeword', str('free')));
        return;
      }
      if (mode === 'label') { priceEl.textContent = o.priceLabel || ''; return; }
      if (mode === 'full') {
        var full = (offer.display && offer.display.showPrice) ? moneyNode(state.variant && state.variant.price) : null;
        if (full) priceEl.appendChild(full); else priceEl.hidden = true;
        return;
      }
      if (mode === 'sale') { paintSale(priceEl, offer, state.variant); return; }
    }
    state.repaint = function () { paintThumb(); paintPrice(); };
    // Programmatic colour change (the trigger variant moved): not a shopper pick,
    // and it does not tick the card.
    state.showVariant = function (v) {
      state.variant = v;
      (state.radios || []).forEach(function (r) { r.inp.checked = (r.v === v); });
      state.repaint();
    };
    state.onUserPick = function (v) {
      state.userPicked = true;
      if (gc) giftPicks[gc.key] = v.id;
    };

    var gated = !o.linkOnly && o.selectable && gatedProduct(p, o.companionHandle);
    if (o.linkOnly || (gated && inStock(p))) {
      var cta = el('a', 'agb-offer__cta', str('viewOffer'));
      cta.setAttribute('href', url);
      row.appendChild(cta);
      if (gated) _dbg('companion is behind the terms gate — link only, never added from the card');
    } else if (!inStock(p)) {
      wrap.classList.add('agb-offer--soldout');
      row.setAttribute('aria-disabled', 'true');
      row.appendChild(el('span', 'agb-offer__soldout', str('soldOut')));
    } else if (o.selectable) {
      row.setAttribute('role', 'button');
      row.setAttribute('tabindex', '0');
      row.setAttribute('aria-pressed', 'false');
      var check = el('span', 'agb-offer__check');
      check.setAttribute('aria-hidden', 'true');
      row.appendChild(check);
      var tickKey = (offer.id || '') + '|' + o.placement;
      var setSel = function (on, silent) {
        if (state.selected === on) return;
        state.selected = on;
        row.classList.toggle('is-selected', on);
        row.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) ticks[tickKey] = { live: !!o.live }; else delete ticks[tickKey];
        if (on) showAddError(state, false);
        if (on && !silent) caeEmit('offer_selected', {
          offer_id: offer.id, type: offer.type, placement: o.placement, live: !!o.live,
          variant_id: state.variant ? state.variant.id : null
        });
      };
      state.setSelected = setSel;
      row.addEventListener('click', function () { setSel(!state.selected); });
      row.addEventListener('keydown', function (e) {
        if (e.target !== row) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); setSel(!state.selected); }
      });
      buildOptions(body, p, state);
      // The same card re-rendered (revalidation): keep the shopper's tick, but
      // never carry a tick made on a FREE card into a paid one.
      var prevTick = ticks[tickKey];
      if (prevTick && (o.live || !prevTick.live) && state.variant && state.variant.available) setSel(true, true);
      else delete ticks[tickKey];
      if (gc && gc.form) {
        // Re-apply when the trigger variant changes: the theme's variant:change on
        // the form, and any change event as a fallback (re-read [name="id"]).
        // The gift colour follows trigger variant tv (unless the shopper picked by hand).
        state.followTrigger = function (tv) {
          if (state.userPicked) return;
          if (!tv || tv === gc.lastTrigger) return;
          gc.lastTrigger = tv;
          var nv = giftVariantFor(p, gc.map, tv);
          if (nv && nv !== state.variant) state.showVariant(nv);
        };
        var reapply = function (e) {
          if (state.userPicked) return;
          var tv = 0;
          try {
            if (e && e.type === 'variant:change' && e.detail && e.detail.variant) tv = parseInt(e.detail.variant.id, 10) || 0;
          } catch (err) {}
          if (!tv) tv = selectedVariantId(gc.form);
          state.followTrigger(tv);
        };
        listen(gc.form, 'variant:change', reapply);
        listen(document, 'change', reapply);
      }
    }

    state.repaint();
    state.timerEl = timerEl;
    state.cdEl = cdEl;
    return state;
  }

  // Variant choice (e.g. buckle colour): default first available, sold-out disabled.
  function buildOptions(body, p, state) {
    if (!p.variants || p.variants.length < 2) return;
    var fs = el('fieldset', 'agb-offer__opts');
    var optName = (p.options && p.options[0] && (p.options[0].name || p.options[0])) || '';
    if (typeof optName !== 'string' || !optName || /^title$/i.test(optName)) optName = str('color');
    var lg = el('legend', 'agb-offer__opts-legend', optName);
    fs.appendChild(lg);
    var group = 'agb-offer-opt-' + (gen) + '-' + Math.floor(Math.random() * 1e6);
    state.radios = [];
    p.variants.forEach(function (v) {
      var lab = el('label', 'agb-offer__opt');
      var inp = el('input');
      inp.setAttribute('type', 'radio');
      inp.setAttribute('name', group);
      inp.setAttribute('value', String(v.id));
      if (!v.available) { inp.disabled = true; inp.setAttribute('disabled', ''); }
      if (state.variant && v.id === state.variant.id) { inp.checked = true; inp.setAttribute('checked', ''); }
      state.radios.push({ inp: inp, v: v });
      lab.appendChild(inp);
      var span = el('span', '', v.public_title || v.title || String(v.id));
      span.setAttribute('dir', 'auto');
      lab.appendChild(span);
      lab.addEventListener('click', function (e) { e.stopPropagation(); });
      inp.addEventListener('change', function () {
        if (!v.available) return;
        if (state.onUserPick) state.onUserPick(v);   // the shopper's touch wins from now on
        state.variant = v;
        state.repaint();
        if (state.setSelected) state.setSelected(true);
      });
      fs.appendChild(lab);
    });
    fs.addEventListener('click', function (e) { e.stopPropagation(); });
    body.appendChild(fs);
  }

  // Sale price line: badge + was -> now. An automatic discount does not change
  // the PDP price, so "now" is computed here. Amount-off is computed only when
  // the shopper sees the store's base currency.
  function paintSale(priceEl, offer, variant) {
    var sale = offer.sale || {};
    var value = Number(sale.value);
    var cur = presentmentCurrency();
    var region = '';
    try { region = window.AGB_STORE_REGION || ''; } catch (e) {}
    var badgeText = null, nowCents = null;
    var price = variant && typeof variant.price === 'number' ? variant.price : null;
    if (sale.kind === 'percentage' && value > 0 && value < 100) {
      badgeText = fmtPercentOff(value);
      if (price != null) nowCents = Math.round(price * (1 - value / 100));
    } else if (sale.kind === 'amount' && value > 0 && BASE_CURRENCY[region] && BASE_CURRENCY[region] === cur) {
      var off = Math.round(value * 100);
      var offTxt = fmtMoney(off);
      if (offTxt) badgeText = '−' + offTxt;
      if (price != null) nowCents = Math.max(0, price - off);
    }
    var badge = el('span', 'agb-offer__badge');
    if (badgeText) badge.appendChild(ltrNode(badgeText)); else badge.textContent = str('sale');
    priceEl.appendChild(badge);
    if (!(offer.display && offer.display.showPrice) || price == null || nowCents == null) return;
    var was = moneyNode(price, 'agb-offer__was');
    var now = moneyNode(nowCents, 'agb-offer__now');
    if (!was || !now) return;
    var sEl = el('s'); sEl.appendChild(was);
    priceEl.appendChild(sEl);
    priceEl.appendChild(document.createTextNode(' '));
    priceEl.appendChild(now);
  }

  // Sale card on the trigger page: the page product itself, price follows the selected variant.
  function buildSaleCard(offer, pageProd, form, w) {
    var wrap = el('div', 'agb-offer agb-offer--sale');
    wrap.setAttribute('data-agb-offer', offer.id || '');
    wrap.setAttribute('data-placement', 'trigger');
    if (isRtl()) wrap.setAttribute('dir', 'rtl');
    var standout = standoutFor(offer);
    if (standout) { var so = el('p', 'agb-offer__standout', standout); so.setAttribute('dir', 'auto'); wrap.appendChild(so); }
    var timerEl = null, cdEl = null;
    if (offer.timing && offer.timing.showTimer && !isNaN(w.end) && w.end > Date.now()) {
      timerEl = el('div', 'agb-offer__timer');
      timerEl.setAttribute('role', 'timer');
      timerEl.appendChild(el('span', 'agb-offer__timer-label', str('endsIn')));
      cdEl = el('span', 'agb-offer__countdown');
      timerEl.appendChild(cdEl);
      wrap.appendChild(timerEl);
      renderCountdown(cdEl, w.end - Date.now());
    }
    var body = el('div', 'agb-offer__body');
    var priceEl = el('p', 'agb-offer__price');
    body.appendChild(priceEl);
    wrap.appendChild(body);
    function current() {
      var idInput = formField(form, 'id');
      var id = idInput ? parseInt(idInput.value, 10) : 0;
      return variantById(pageProd, id) || firstAvailable(pageProd) || pageProd.variants[0];
    }
    function paint() {
      while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);
      paintSale(priceEl, offer, current());
    }
    paint();
    var onVariant = function () { setTimeout(paint, 0); };
    listen(document, 'variant:change', onVariant);
    return { wrap: wrap, timerEl: timerEl, cdEl: cdEl };
  }

  // ===== Mount + lifecycle =================================================

  function mount(node, form) {
    var mp = mountPoint(form);
    mp.parent.insertBefore(node, mp.before);
    track().nodes.push(node);
  }

  // One interval per card: tick the countdown to endsAt, remove at hideAt.
  function runClock(offer, placement, w, card, onHidden) {
    var done = false;
    function tick() {
      if (done) return;
      var now = Date.now();
      if (!isNaN(w.hide) && now >= w.hide) {
        done = true;
        // Gone from the page = disarmed: no add path may add for it any more.
        if (card.setSelected) card.setSelected(false); else card.selected = false;
        if (armed && armed.state === card) armed = null;
        if (card.wrap && card.wrap.parentNode) card.wrap.parentNode.removeChild(card.wrap);
        caeEmit('offer_expired', { offer_id: offer.id, type: offer.type, placement: placement });
        if (onHidden) onHidden();
        return;
      }
      if (!card.cdEl) return;
      if (now >= w.end) {
        // The countdown ended but the card stays on until hideAt: the timer
        // leaves the card rather than sit at 00:00:00.
        if (card.timerEl && card.timerEl.parentNode) card.timerEl.parentNode.removeChild(card.timerEl);
        card.timerEl = null;
        card.cdEl = null;
        return;
      }
      renderCountdown(card.cdEl, w.end - now);
    }
    track().intervals.push(setInterval(tick, 1000));
  }

  // Shopper-visible add failure inside the card (textContent only); cleared on the next try.
  function showAddError(state, on) {
    var host = state && state.wrap && state.wrap.querySelector('.agb-offer__body');
    if (!host) return;
    var msg = host.querySelector('.agb-offer__error');
    if (!on) { if (msg && msg.parentNode) msg.parentNode.removeChild(msg); return; }
    if (!msg) {
      msg = el('p', 'agb-offer__error');
      msg.setAttribute('role', 'alert');
      host.appendChild(msg);
    }
    msg.textContent = str('addError');
  }
  // The page item went in but the companion was refused (sold out since the
  // page loaded, a quantity cap): untick so the next add of the page product
  // does not repeat it, and say which product was not added. No "try again" —
  // the only retry is the page's Add to cart, which would add the page item again.
  function companionRefused(state) {
    if (!state) return;
    if (state.setSelected) state.setSelected(false); else state.selected = false;
    var host = state.wrap && state.wrap.querySelector('.agb-offer__body');
    if (!host) return;
    var msg = host.querySelector('.agb-offer__error');
    if (!msg) {
      msg = el('p', 'agb-offer__error');
      msg.setAttribute('role', 'alert');
      host.appendChild(msg);
    }
    while (msg.firstChild) msg.removeChild(msg.firstChild);
    var p = state.product || {};
    fill(msg, str('compRefused'), { name: nameNode(p.title || p.handle || '') });
  }
  // Still on the page? (a removed card never adds)
  function onPage(state) {
    var w = state && state.wrap;
    if (!w) return false;
    if (typeof w.isConnected === 'boolean') return w.isConnected;
    for (var n = w; n; n = n.parentNode) if (n === document || n === document.documentElement) return true;
    return false;
  }

  function bindSubmit(form, offer, placement, state) {
    var handler = function (e) {
      if (!state.selected || !onPage(state)) return;
      if (e.defaultPrevented) return;              // another gate already stopped this add
      if (freeCcwPresent()) return;                // its own interceptor owns this page
      var gate = form.querySelector('[data-terms-checkbox]');
      if (gate && !gate.checked) return;           // let the terms gate block it
      var idInput = formField(form, 'id');
      var pageVariant = idInput ? parseInt(idInput.value, 10) : 0;
      var companion = state.variant && state.variant.available ? state.variant.id : 0;
      if (!pageVariant || !companion) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var btns = submitButtons(form);
      btns.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });
      showAddError(state, false);
      var pageItem = { id: pageVariant, quantity: pageQuantity(form) };
      var props = formProperties(form);
      if (props) pageItem.properties = props;
      var done = function () { btns.forEach(function (b) { b.removeAttribute('aria-busy'); }); };
      addItems([pageItem, { id: companion, quantity: 1 }])
        .then(afterAdd, function (err) {
          ereReport(err, offer.id, pageHandle(), false);
          return addItems([pageItem]).then(function (r) {
            if (err && err.http >= 400 && err.http < 500) companionRefused(state);
            return afterAdd(r);
          }, function (err2) {
            ereReport(err2, offer.id, pageHandle(), true);
            // The add was taken over from the theme, so its own error UI never runs: say it here.
            showAddError(state, true);
          });
        })
        .then(done, done);
    };
    listen(form, 'submit', handler, true);
  }

  // ===== Every other add path: ride the page's own add request =============

  var armed = null;          // { state, offer, placement, form, pageIds, compIds } of the mounted card
  var selfPost = 0;          // > 0 while addItems() itself calls fetch
  var prevFetch = null;      // the fetch the rider wraps

  function idsOf(p) {
    var m = {};
    ((p && p.variants) || []).forEach(function (v) { if (v && v.id) m[String(v.id)] = 1; });
    return m;
  }
  function gidNum(g) { var m = /(\d+)\s*$/.exec(String(g || '')); return m ? m[1] : ''; }
  function reqUrl(input) {
    if (typeof input === 'string') return input;
    try { return String((input && (input.url || input.href)) || input || ''); } catch (e) { return ''; }
  }
  // The companion to add next to trigger variant triggerId, or 0.
  function armedCompanion(a, triggerId) {
    if (!a || armed !== a || !a.state.selected || !onPage(a.state) || freeCcwPresent()) return 0;
    if (triggerId && a.state.followTrigger) a.state.followTrigger(triggerId);
    var v = a.state.variant;
    return v && v.available ? v.id : 0;
  }
  // Scan ids: the first page-product id, and whether a companion line is already there.
  function scan(a, ids) {
    var trig = 0, hasComp = false;
    ids.forEach(function (k) {
      k = String(k == null ? '' : k);
      if (!k) return;
      if (!trig && a.pageIds[k]) trig = parseInt(k, 10) || 0;
      if (a.compIds[k]) hasComp = true;
    });
    return { trig: trig, hasComp: hasComp };
  }

  // null = leave the request alone; else { a, init (augmented), kind }.
  function planRide(input, init) {
    var a = armed;
    if (selfPost > 0 || !a || !a.state.selected) return null;
    if (!init || typeof init.body !== 'string') return null;      // FormData / Request: not ours
    if (String(init.method || 'GET').toUpperCase() !== 'POST') return null;
    var url = reqUrl(input);
    var body = JSON.parse(init.body);
    if (!body || typeof body !== 'object') return null;
    var sc, comp, nb;
    if (/\/cart\/add(\.js)?(\?|#|$)/.test(url) && Array.isArray(body.items)) {
      sc = scan(a, body.items.map(function (it) { return it && it.id; }));
      if (!sc.trig || sc.hasComp) return null;
      comp = armedCompanion(a, sc.trig);
      if (!comp) return null;
      nb = Object.assign({}, body, { items: body.items.concat([{ id: comp, quantity: 1 }]) });
      return { a: a, kind: 'ajax', init: Object.assign({}, init, { body: JSON.stringify(nb) }) };
    }
    // The builder's Storefront branch (only with a Storefront token): cartLinesAdd.
    if (/graphql/.test(url) && typeof body.query === 'string' && /\bcartLinesAdd\b/.test(body.query) &&
        body.variables && Array.isArray(body.variables.lines)) {
      sc = scan(a, body.variables.lines.map(function (l) { return gidNum(l && l.merchandiseId); }));
      if (!sc.trig || sc.hasComp) return null;
      comp = armedCompanion(a, sc.trig);
      if (!comp) return null;
      var vars = Object.assign({}, body.variables, {
        lines: body.variables.lines.concat([{ quantity: 1, merchandiseId: 'gid://shopify/ProductVariant/' + comp }])
      });
      nb = Object.assign({}, body, { variables: vars });
      return { a: a, kind: 'storefront', init: Object.assign({}, init, { body: JSON.stringify(nb) }) };
    }
    return null;
  }

  function sfErrors(j) {
    var e = j && (j.errors || (j.data && j.data.cartLinesAdd && j.data.cartLinesAdd.userErrors));
    return Array.isArray(e) ? e.length > 0 : !!e;
  }

  function rider(input, init) {
    var args = arguments, plan = null;
    try { plan = planRide(input, init); } catch (e) { plan = null; }
    if (!plan) return prevFetch.apply(window, args);
    var a = plan.a;
    function ok() { try { if (armed === a) showAddError(a.state, false); } catch (e) {} }
    // Did this response add the lines? cartLinesAdd answers 200 with userErrors
    // and adds NOTHING, so the Storefront branch reads a copy of the body.
    // Never rejects.
    function landed(r) {
      if (!r || !r.ok) return Promise.resolve(false);
      if (plan.kind !== 'storefront') return Promise.resolve(true);
      try {
        if (typeof r.clone !== 'function') return Promise.resolve(true);
        return r.clone().json().then(function (j) { return !sfErrors(j); }, function () { return true; });
      } catch (e) { return Promise.resolve(true); }
    }
    // The ride was refused (nothing committed): report it, then send the
    // shopper's ORIGINAL request (its own success / error handling runs on that
    // answer). It landing means the companion was the line refused.
    function resend(err) {
      try { ereReport(err, a.offer.id, pageHandle(), false); } catch (e) {}
      return prevFetch.apply(window, args).then(function (r2) {
        return landed(r2).then(function (yes) {
          try { if (yes && armed === a) companionRefused(a.state); } catch (e) {}
          return r2;
        });
      });
    }
    // A definite refusal commits nothing: Shopify's 4xx (422 sold out / cap,
    // 400, 429), or cartLinesAdd answering 200 with errors.
    function refused(r) {
      return !!r && (r.ok || (typeof r.status === 'number' && r.status >= 400 && r.status < 500));
    }
    // Unknown outcome (rejection, status 0, 5xx): the add may already be in the
    // cart, so nothing is re-sent. A rejection becomes a not-ok answer so the
    // path's own error branch runs (the builder's catch would leave its button
    // stuck on "Adding...").
    function netFail() {
      if (typeof Response !== 'function') return null;
      try {
        return new Response(JSON.stringify({ status: 503, message: 'Network error', description: str('addError') }),
          { status: 503, headers: { 'Content-Type': 'application/json' } });
      } catch (e) { return null; }
    }
    function unknownOutcome(err) {
      try { ereReport(err, a.offer.id, pageHandle(), false); } catch (e) {}
      var r = netFail();
      return r || Promise.reject(err);
    }
    function rideErr(r) {
      var e = new Error(r && r.ok ? 'companion ride: cartLinesAdd errors' : 'companion ride HTTP ' + (r ? r.status : 'none'));
      e.http = r ? r.status : 0;
      return e;
    }
    var p;
    try { p = prevFetch.call(window, input, plan.init); } catch (e) { return resend(e); }   // never sent
    return Promise.resolve(p).then(function (r) {
      return landed(r).then(function (yes) {
        if (yes) { ok(); return r; }
        if (refused(r)) return resend(rideErr(r));
        try { ereReport(rideErr(r), a.offer.id, pageHandle(), false); } catch (e) {}
        // A network monitor's stand-in {status:0} is not a Response: hand the
        // path a real not-ok one so its own error branch can read it.
        if (!r || typeof r.json !== 'function' || !r.status) return netFail() || r;
        return r;
      });
    }, unknownOutcome);
  }

  function installRider() {
    if (prevFetch) return;
    if (typeof window.fetch !== 'function') return;
    prevFetch = window.fetch;
    rider.__agbOffersRider = true;
    window.fetch = rider;
  }

  // Bound where bindSubmit is (a selectable card whose companion is in stock).
  function bindAddPaths(form, offer, placement, state, pageProd) {
    installRider();
    var a = {
      state: state, offer: offer, placement: placement, form: form,
      pageIds: idsOf(pageProd), compIds: idsOf(state.product)
    };
    armed = a;
    // Adds that bypass fetch (the ask card's XHR) announce themselves with
    // variant:add {items}: add the companion alone, then refresh the drawer.
    listen(document, 'variant:add', function (e) {
      if (armed !== a || !a.state.selected || freeCcwPresent()) return;
      var gate = form.querySelector('[data-terms-checkbox]');
      if (gate && !gate.checked) return;                    // same gate rule as bindSubmit
      var items = e && e.detail && e.detail.items;
      if (!items) return;
      if (!Array.isArray(items)) items = [items];
      var ids = [];
      items.forEach(function (it) { if (it) { ids.push(it.variant_id); ids.push(it.id); } });
      var sc = scan(a, ids);
      if (!sc.trig || sc.hasComp) return;                   // not the page product / already carried (a ridden response)
      var comp = armedCompanion(a, sc.trig);
      if (!comp) return;
      addItems([{ id: comp, quantity: 1 }], true).then(function () {
        if (armed === a) showAddError(a.state, false);
        try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (err) {}
      }, function (err) {
        ereReport(err, offer.id, pageHandle(), false);
        if (armed === a) companionRefused(a.state);
      });
    });
  }

  function emitShown(offer, placement, live) {
    caeEmit('offer_shown', { offer_id: offer.id, type: offer.type, placement: placement, live: !!live });
  }

  // Gift page note: "FREE when you add it with …" / "✓ FREE — already in your cart".
  function initGiftNote(offer, triggerProd) {
    var note = el('p', 'agb-offer-note');
    note.setAttribute('data-agb-offer-note', offer.id || '');
    if (isRtl()) note.setAttribute('dir', 'rtl');
    var priceRef = document.querySelector('.price-list--product') ||
      document.querySelector('.product-info .price-list, .product__price, .price');
    if (priceRef && priceRef.parentNode) priceRef.parentNode.insertBefore(note, priceRef.nextSibling);
    else {
      var form = productForm();
      if (!form) return;
      var mp = mountPoint(form);
      mp.parent.insertBefore(note, mp.before);
    }
    track().nodes.push(note);
    var tid = parseInt(offer.trigger && offer.trigger.productId, 10);
    var myGen = gen;
    function paint(cart) {
      if (myGen !== gen) return;
      var free = !!(cart && cart.items && cart.items.some(function (it) { return it && it.product_id === tid; }));
      note.classList.toggle('is-free', free);
      while (note.firstChild) note.removeChild(note.firstChild);
      fill(note, free ? str('giftNoteInCart') : str('giftNote'), { trigger: nameNode(triggerProd.title || offer.trigger.handle) });
    }
    function refresh() { getCart().then(paint); }
    refresh();
    listen(document, 'cart:change', refresh);
    // This module's own combined add dispatches only cart:refresh (Prestige's
    // cart:refresh path never fires cart:change), so listen for it too.
    listen(document, 'cart:refresh', refresh);
    listen(window, 'pageshow', refresh);
  }

  // ===== Decide what this page shows =======================================

  function validOffer(o) {
    return o && typeof o === 'object' && o.enabled === true &&
      (o.type === 'free_gift' || o.type === 'sale') &&
      o.trigger && typeof o.trigger.handle === 'string' && o.trigger.handle &&
      o.timing && o.display &&
      (o.type !== 'free_gift' || (o.gift && typeof o.gift.handle === 'string' && o.gift.handle));
  }

  function pick(cfg, confirmed) {
    var pid = pageProductId(), handle = pageHandle();
    if (!handle && !pid) return null;
    var now = Date.now(), active = [], ended = null, upcoming = [];
    (cfg.offers || []).forEach(function (o) {
      if (!validOffer(o)) return;
      var w = windowOf(o);
      if (isNaN(w.start) || isNaN(w.end) || isNaN(w.hide)) return;
      var role = null;
      if (isPage(o.trigger, pid, handle)) role = 'trigger';
      else if (o.type === 'free_gift' && isPage(o.gift, pid, handle)) role = 'gift';
      else if (handle && (o.display.cardOn || []).some(function (h) { return String(h || '').toLowerCase() === handle; })) role = 'card';
      if (!role) return;
      var live = confirmed && o.live === true;
      if (now < w.start) { upcoming.push(w.start); return; }
      if (now < w.hide) {
        // Known not to render without a product fetch: never let it shadow a later offer.
        if (o.type === 'sale' && role === 'trigger' && !live) return;
        if (o.type === 'free_gift' && role === 'gift' && !live) return;
        active.push({ offer: o, role: role, live: live, w: w });
        return;
      }
      if (role === 'trigger' && o.type === 'free_gift' && (o.display.afterEnd || 'cross_sell') === 'cross_sell' && !ended) {
        ended = { offer: o, role: 'after_end', live: false, w: w };
      }
    });
    // Config order = priority; a running offer beats an ended one's cross-sell.
    return { candidates: ended ? active.concat([ended]) : active, upcoming: upcoming };
  }

  function render(cfg, confirmed) {
    teardown();
    var myGen = ++gen;
    if (!cfg || cfg.schemaVersion !== 1 || cfg.enabled !== true) { _dbg('config off / invalid'); return; }
    if (freeCcwPresent()) { _dbg('free-ccw card on page — standing down'); return; }
    var res = pick(cfg, confirmed);
    if (!res) return;
    // An offer starting while the page is open renders at its start — after the
    // server confirms it again (the page-load confirmation may be hours old).
    var now = Date.now();
    res.upcoming.forEach(function (t) { later(function () { if (myGen === gen) loadConfig(); }, t - now + 50); });
    var list = res.candidates;
    if (!list.length) { _dbg('no offer for this page'); return; }
    var form = productForm();
    if (!form) { _dbg('no product form'); return; }
    // Config order = priority; an offer that cannot render here yields to the next.
    (function attempt(i) {
      if (myGen !== gen || i >= list.length) return;
      var choice = list[i], offer = choice.offer;
      var needGift = offer.type === 'free_gift';
      Promise.all([
        fetchProduct(offer.trigger.handle),
        needGift ? fetchProduct(offer.gift.handle) : Promise.resolve(null)
      ]).then(function (r) {
        if (myGen !== gen) return;
        var trig = r[0], gift = r[1];
        if (trig.status === 'error') ereReport(new Error('trigger product fetch failed'), offer.id, offer.trigger.handle);
        if (gift && gift.status === 'error') ereReport(new Error('gift product fetch failed'), offer.id, offer.gift.handle);
        if (trig.status !== 'ok') { _dbg('trigger product unavailable', trig.status); return attempt(i + 1); }
        if (!trig.product.available && !inStock(trig.product)) { _dbg('trigger not purchasable'); return attempt(i + 1); }
        if (needGift && gift.status !== 'ok') { _dbg('gift product missing for this store — nothing rendered'); return attempt(i + 1); }
        if (freeCcwPresent()) return;
        injectStyle();
        if (!show(choice, trig.product, gift && gift.product, form)) attempt(i + 1);
      }).catch(function (err) { ereReport(err, offer.id, pageHandle()); });
    })(0);
  }

  // Returns true when a card was mounted; false = nothing for this offer (try the next).
  function show(choice, trigProd, giftProd, form) {
    var offer = choice.offer, w = choice.w, live = choice.live, role = choice.role;
    var showTimer = !!(offer.timing && offer.timing.showTimer);
    var card = null;
    // Trigger page, gift card: its colour follows the trigger variant (gift.colourMap).
    var cmap = (offer.type === 'free_gift') ? colourMapOf(offer) : null;
    var giftColour = cmap ? { map: cmap, form: form, key: (offer.id || '') + '|' + offer.gift.handle } : null;
    var onHidden = function () {
      // Trigger page: after hideAt, revalidate and re-pick (the plain cross-sell, if configured).
      if (role === 'trigger') { var g = gen; later(function () { if (g === gen) loadConfig(); }, 0); }
    };

    if (role === 'after_end') {
      card = buildCard({
        offer: offer, placement: 'after_end', live: false, companion: giftProd,
        companionHandle: offer.gift.handle, claimFree: false, eyebrow: str('completeSetup'),
        showTimer: false, selectable: true, priceMode: 'full', giftColour: giftColour
      });
      mount(card.wrap, form);
      if (card.setSelected) {   // selectable: in stock and not behind the terms gate
        bindSubmit(form, offer, 'after_end', card);
        bindAddPaths(form, offer, 'after_end', card, trigProd);
      }
      emitShown(offer, 'after_end', false);
      return true;
    }

    if (role === 'trigger' && offer.type === 'sale') {
      if (!live) { _dbg('sale not live — no card'); return false; }
      card = buildSaleCard(offer, trigProd, form, w);
      mount(card.wrap, form);
      runClock(offer, 'trigger', w, card, null);
      emitShown(offer, 'trigger', true);
      return true;
    }

    if (role === 'trigger') {   // free_gift
      if (live) {
        card = buildCard({
          offer: offer, placement: 'trigger', live: true, companion: giftProd,
          companionHandle: offer.gift.handle, claimFree: true, eyebrow: str('addFree'),
          showTimer: showTimer, endsMs: w.end, selectable: true, priceMode: 'free', giftColour: giftColour
        });
      } else {
        card = buildCard({
          offer: offer, placement: 'trigger', live: false, companion: giftProd,
          companionHandle: offer.gift.handle, claimFree: false, eyebrow: str('completeSetup'),
          showTimer: false, selectable: true, priceMode: 'full', giftColour: giftColour
        });
      }
      mount(card.wrap, form);
      if (card.setSelected) {   // selectable: in stock and not behind the terms gate
        bindSubmit(form, offer, 'trigger', card);
        bindAddPaths(form, offer, 'trigger', card, trigProd);
      }
      runClock(offer, 'trigger', w, card, onHidden);
      emitShown(offer, 'trigger', live);
      return true;
    }

    if (role === 'gift') {      // free_gift, gift PDP: only a confirmed, fully in-stock offer
      if (!live || !inStock(giftProd) || !inStock(trigProd)) { _dbg('gift page: offer not live / stock'); return false; }
      var eb = fill(el('span'), str('giftPage'), {
        trigger: nameNode(trigProd.title || offer.trigger.handle),
        gift: nameNode(giftProd.title || offer.gift.handle)
      });
      card = buildCard({
        offer: offer, placement: 'gift', live: true, companion: trigProd,
        companionHandle: offer.trigger.handle, claimFree: true, eyebrow: eb,
        showTimer: showTimer, endsMs: w.end, selectable: true, priceMode: 'full'
      });
      mount(card.wrap, form);
      if (card.setSelected) {
        bindSubmit(form, offer, 'gift', card);
        bindAddPaths(form, offer, 'gift', card, giftProd);
      }
      initGiftNote(offer, trigProd);
      runClock(offer, 'gift', w, card, function () {
        var notes = document.querySelectorAll('[data-agb-offer-note]');
        for (var i = 0; i < notes.length; i++) if (notes[i].parentNode) notes[i].parentNode.removeChild(notes[i]);
      });
      emitShown(offer, 'gift', true);
      return true;
    }

    if (role === 'card') {      // cardOn PDP: a compact link to the trigger PDP; never adds
      var eyebrow, priceMode = 'full', claim = false;
      if (offer.type === 'free_gift' && live) {
        if (!inStock(giftProd)) { _dbg('card: gift sold out — nothing rendered'); return false; }
        eyebrow = fill(el('span'), str('cardFree'), {
          gift: nameNode(giftProd.title || offer.gift.handle),
          trigger: nameNode(trigProd.title || offer.trigger.handle)
        });
        claim = true;
      } else if (offer.type === 'sale' && live) {
        eyebrow = null; priceMode = 'sale'; claim = true;
      } else {
        eyebrow = str('completeSetup');
      }
      card = buildCard({
        offer: offer, placement: 'card', live: live, companion: trigProd,
        companionHandle: offer.trigger.handle, claimFree: claim && offer.type === 'free_gift', eyebrow: eyebrow,
        showTimer: live && showTimer, endsMs: w.end, selectable: false, linkOnly: true, priceMode: priceMode
      });
      mount(card.wrap, form);
      runClock(offer, 'card', w, card, null);
      emitShown(offer, 'card', live);
      return true;
    }
    return false;
  }

  // ===== Config: TTL 0, ETag revalidation, cached fallback without claims =====

  function readCache() {
    try {
      var raw = localStorage.getItem(LS_CONFIG_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      return (c && c.schemaVersion === 1) ? c : null;
    } catch (e) { return null; }
  }
  // The copy and its ETag are ONE unit: a 304 against an ETag means "your copy is
  // current", so an ETag must never sit next to a copy it was not issued for
  // (that 304 would confirm a stale live:true). Drop the old ETag first, store
  // the copy, and only then store the new ETag; a failed copy write removes the
  // stale copy too, so the next load asks for a full 200.
  function writeCache(cfg, etag) {
    try { localStorage.removeItem(LS_CONFIG_ETAG); } catch (e) {}
    var stored = false;
    try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(cfg)); stored = true; } catch (e) {}
    if (!stored) { try { localStorage.removeItem(LS_CONFIG_KEY); } catch (e) {} return; }
    if (etag) { try { localStorage.setItem(LS_CONFIG_ETAG, etag); } catch (e) {} }
  }

  var loadSeq = 0;   // a newer load (timed revalidation) supersedes an older one still in flight
  function loadConfig() {
    var mySeq = ++loadSeq;
    var base = window.AGB_OFFERS_CONFIG_URL;
    if (!base) { _dbg('no AGB_OFFERS_CONFIG_URL — nothing to do'); return; }
    var endpoint = ENV === 'preview' ? base + (base.indexOf('?') === -1 ? '?' : '&') + 'env=preview' : base;
    var cached = readCache();
    var headers = { 'Accept': 'application/json' };
    try {
      var etag = cached ? localStorage.getItem(LS_CONFIG_ETAG) : null;
      if (etag) headers['If-None-Match'] = etag;
    } catch (e) {}
    try { var rgn = window.AGB_STORE_REGION || ''; if (rgn) headers['X-Store-Region'] = rgn; } catch (e) {}

    var settled = false, fellBack = false;
    // Could not confirm right now: the cached copy, with every offer treated as not live.
    function fallback(reason) {
      if (settled || fellBack || mySeq !== loadSeq) return;
      fellBack = true;
      _dbg('config fallback (' + reason + ') — cached copy, no claims');
      if (cached) render(cached, false);
    }
    var to = setTimeout(function () { fallback('timeout'); }, FETCH_TIMEOUT_MS);

    fetch(endpoint, { headers: headers, cache: 'no-cache' })
      .then(function (res) {
        if (res.status === 304) {
          if (!cached) throw new Error('304 without a cached config');
          return { cfg: cached };
        }
        if (!res.ok) { var he = new Error('offers-config HTTP ' + res.status); he.http = res.status; throw he; }
        return res.json().then(function (body) {
          var c = body && (body.config || (body.schemaVersion ? body : null));
          if (!c || c.schemaVersion !== 1) throw new Error('offers-config: unexpected shape');
          var et = null;
          try { et = res.headers.get('ETag'); } catch (e) {}
          writeCache(c, et);
          return { cfg: c };
        });
      })
      .then(function (r) {
        settled = true;
        clearTimeout(to);
        if (mySeq !== loadSeq) return;
        render(r.cfg, true);   // also upgrades a card the timeout fallback rendered without claims
      })
      .catch(function (err) {
        clearTimeout(to);
        // 404 = this backend does not serve offers (yet): quiet, not an incident.
        if (!(err && err.http === 404)) ereReport(err, null, pageHandle());
        else _dbg('offers-config 404 — engine not served by this backend');
        fallback('error');
      });
  }

  function init() {
    if (pageHandle() === '' && !pageProductId()) return;   // PDPs only
    loadConfig();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
